import { Icon } from 'src/components/Icon';
import { SUPER_PROJECT_NAME, SVG_SIZE_M } from 'src/constants';
import {
  CalendarDots,
  CodeBlock,
  FileMagnifyingGlass,
  Images,
  ListMagnifyingGlass,
} from '@phosphor-icons/react';
import { ChatGPTLogo } from 'src/images/logos/chat-gpt';
import { ABTestTier, NinjaSubscriptionPlan } from 'src/types';
import { ReactElement } from 'react';
import { PlanProduct } from 'src/types/wallet/models/PlanProduct';

export const BASIC_TIER_MODELS_AND_SKILLS = [
  {
    title: 'Amazon Nova Pro',
    icon: <Icon type="nova" size={SVG_SIZE_M} />,
  },
  {
    title: 'Research',
    icon: <ListMagnifyingGlass size={SVG_SIZE_M} />,
  },
  {
    title: 'Claude 3.7 Sonnet',
    icon: <Icon type="claudeLogo" size={SVG_SIZE_M} />,
  },
  {
    title: 'FLUX 1.1 Pro',
    icon: <Icon type="flux" size={SVG_SIZE_M} />,
  },
  {
    title: 'Scheduler',
    icon: <CalendarDots size={SVG_SIZE_M} />,
  },
  {
    title: 'Stable Diffusion XL 1.0',
    icon: <Icon type="bedrockStableLogo" size={SVG_SIZE_M} />,
  },
  {
    title: 'Image Generation',
    icon: <Images size={SVG_SIZE_M} />,
  },
  {
    title: 'DeepSeek R1',
    icon: <Icon type="deepSeekLogo" size={SVG_SIZE_M} />,
  },
  {
    title: 'Coding',
    icon: <CodeBlock size={SVG_SIZE_M} />,
  },
  {
    title: 'Gemini 1.5 Pro',
    icon: <Icon type="googleGeminiProLogo" size={SVG_SIZE_M} />,
  },
  {
    title: 'File Analysis',
    icon: <FileMagnifyingGlass size={SVG_SIZE_M} />,
  },
  {
    title: 'GPT–4o Mini',
    icon: <Icon type="chatGPTLogo" size={SVG_SIZE_M} />,
  },
];

// const STANDARD_FEATURES = [
//   {
//     id: '1',
//     title: <span>Medium quality image generation</span>,
//   },
//   {
//     id: '2',
//     title: <span>Generate podcast</span>,
//   },
//   {
//     id: '3',
//     title: (
//       <span className="nj-feature-bold-item">
//         <span className="colored">Unlimited</span> access to 2 AI models
//       </span>
//     ),
//   },
// ];

const ULTRA_FEATURES = [
  {
    id: '1',
    title: <span>High quality image generation</span>,
  },
  {
    id: '2',
    title: <span>Video generation</span>,
  },
  {
    id: '3',
    title: <span>File upload analysis</span>,
  },
  {
    id: '4',
    title: <span>SuperAgent–R 2.0 (reasoning model)</span>,
  },
  {
    id: '5',
    title: <span>Pay-per-use API</span>,
  },
  {
    id: '7',
    title: (
      <span className="nj-feature-bold-item">
        <span className="colored">Unlimited</span> access to 30+ AI models
      </span>
    ),
  },
];

// const STANDARD_MODELS = [
//   {
//     id: '1',
//     icon: <NinjaAgentsOutlinedIcon width={SVG_SIZE_M} height={SVG_SIZE_M} />,
//     text: <span>Ninja auto-selects text models</span>,
//   },
//   {
//     id: '2',
//     icon: <NinjaAgentsOutlinedIcon width={SVG_SIZE_M} height={SVG_SIZE_M} />,
//     text: <span>Ninja auto-selects image models</span>,
//   },
// ];

const ULTRA_MODELS = [
  {
    id: '1',
    icon: <Icon type="deepSeekLogo" size={SVG_SIZE_M} />,
    text: <span>DeepSeek R1</span>,
    isShowImageIcon: false,
  },
  {
    id: '2',
    icon: <ChatGPTLogo size={SVG_SIZE_M} />,
    text: <span>OpenAI GPT–4.1</span>,
    isShowImageIcon: false,
  },
  {
    id: '3',
    icon: <Icon type="claudeLogo" size={SVG_SIZE_M} />,
    text: <span>Claude 3.7 Sonnet</span>,
    isShowImageIcon: false,
  },
];

// todo use price from BE data
export const BASIC_WELCOME_UPSELL_MODAL_PLANS: {
  id: NinjaSubscriptionPlan;
  title: string;
  price: number;
  features: {
    id: string;
    title: JSX.Element;
  }[];
  models: {
    id: string;
    icon: ReactElement;
    text: ReactElement;
    isShowImageIcon?: boolean;
  }[];
  description: ReactElement;
  availableFor: ABTestTier[];
}[] = [
  {
    id: NinjaSubscriptionPlan.ULTRA,
    title: 'Ultra',
    price: 15,
    features: ULTRA_FEATURES,
    models: ULTRA_MODELS,
    description: (
      <span>
        <span className="colored">Unlimited tasks</span> with 30+ AI models
      </span>
    ),
    availableFor: ['ab-standard-ultra', 'ab-ultra'],
  },
  {
    id: NinjaSubscriptionPlan.SUPERNINJA_ULTRA,
    title: 'Max',
    price: 100,
    features: ULTRA_FEATURES,
    models: ULTRA_MODELS,
    description: (
      <span>
        <span className="colored">Unlimited tasks</span> with 30+ AI models
      </span>
    ),
    availableFor: ['ab-standard-ultra', 'ab-ultra'],
  },
];

const welcomeIconSize = 48;

export const FREE_TIER_WELCOME_MODAL_NEW_PLANS = {
  [PlanProduct.NINJA]: {
    title: 'Ninja – Chat agent',
    icon: <Icon type="ninjaMainIcon" size={welcomeIconSize} />,
    description:
      'AI for writing, coding, image, deep research, and more with access to premium AI models',
    features: [
      'Unlimited Ninja task',
      '40+ premium text & image AI models',
      'Writing and coding',
      'Image generation',
      'Deep research',
    ],
    minPrice: 5, // todo remove this and use price from BE
    maxPrice: 15,
    isSuperNinja: false,
  },
  [PlanProduct.SUPERNINJA]: {
    title: `${SUPER_PROJECT_NAME} – Autonomous AI agent with it’s own computer`,
    icon: <Icon type="superNinjaMainIcon" size={welcomeIconSize} />,
    description: 'Turn any idea into finished tasks',
    features: [
      'SuperNinja Autonomous AI tasks',
      'End-to-End coding',
      <span>GitHub integration</span>,
      'Advanced research & insight',
      'Website creation',
      'Document, slides and spreadsheet creation',
    ],
    minPrice: 25, // todo remove this and use price from BE
    maxPrice: 100,
    isSuperNinja: true,
  },
};
